import { ExternalConnectionsProviders } from '@griply/types';
import { CircularProgress, Typography } from '@mui/material';
import { useExternalCalendarConnections } from 'features/external-connections';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Google, Outlook, Plus } from 'shared/assets/icons';
import { ExternalCalendarConnection } from 'shared/components/connected/external-calendar-connection';
import { Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useRedirectToExternalAuthMutation } from 'shared/hooks/queries/use-redirect-to-external-auth-mutation';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import * as Styled from './calendar-external-connections-list.style';

export const CalendarExternalConnectionsList: React.FC = () => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const externalCalendars = useExternalCalendarConnections();
  const { mutate: redirectToExternalAuth, isPending } =
    useRedirectToExternalAuthMutation();

  const onAddGoogle = () => {
    !isPending &&
      redirectToExternalAuth({ provider: ExternalConnectionsProviders.Google });
    closeMenu();
  };
  const onAddOutlook = () => {
    !isPending &&
      redirectToExternalAuth({
        provider: ExternalConnectionsProviders.Outlook,
      });
    closeMenu();
  };

  return (
    <Styled.Section>
      <Typography variant="h6" component="h3">
        {t('calendar.accounts.title')}
      </Typography>

      <Styled.List>
        {!!externalCalendars.length && (
          <Styled.HeaderRow>
            <Styled.AccountColumnLabel>
              {t(
                'forms.user-settings.sections.calendar.accounts.columns.account',
              )}
            </Styled.AccountColumnLabel>
            <Styled.CalendarsColumnLabel>
              {t(
                'forms.user-settings.sections.calendar.accounts.columns.visible-calendars',
              )}
            </Styled.CalendarsColumnLabel>
          </Styled.HeaderRow>
        )}

        {externalCalendars.map((calendarConnection) => (
          <Styled.ListItem key={calendarConnection.id}>
            <ExternalCalendarConnection connection={calendarConnection} />
          </Styled.ListItem>
        ))}

        <Styled.ListItem>
          <Styled.AddButton
            ref={buttonRef}
            type="button"
            variant={Variants.Text}
            size={Sizes.Medium}
            color={Colors.Primary}
            startIcon={Plus}
            onClick={openMenu}
            disabled={isPending}
          >
            {t('calendar.accounts.connect')}
            {isPending && <CircularProgress size={12} color="inherit" />}
          </Styled.AddButton>

          {menuOpen && (
            <PopupMenu referenceElement={buttonRef} onOutside={closeMenu}>
              <PopupMenuList>
                <PopupMenuListItem>
                  <PopupMenuButton
                    onClick={onAddGoogle}
                    start={<Icon icon={Google} />}
                  >
                    {t(
                      'forms.user-settings.sections.calendar.add-external.google.label',
                    )}
                  </PopupMenuButton>
                </PopupMenuListItem>
                <PopupMenuListItem>
                  <PopupMenuButton
                    onClick={onAddOutlook}
                    start={<Icon icon={Outlook} />}
                  >
                    {t(
                      'forms.user-settings.sections.calendar.add-external.outlook.label',
                    )}
                  </PopupMenuButton>
                </PopupMenuListItem>
              </PopupMenuList>
            </PopupMenu>
          )}
        </Styled.ListItem>
      </Styled.List>
    </Styled.Section>
  );
};
